import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="device-search-form"
export default class extends Controller {
  static targets = [ "searchQuery", "searchType", "searchScopes", "requirementsSection", "caseReferenceField", "expirationField", "scopeOption" ]

  connect() {
    // Auto-detect search type on initial load if there's a value
    if (this.hasSearchQueryTarget && this.searchQueryTarget.value) {
      this.detectType()
    }
    
    // Add click handlers for disabled scope options
    this.scopeOptionTargets.forEach(option => {
      option.addEventListener('click', this.handleScopeClick.bind(this))
    })
  }

  handleScopeClick(event) {
    const label = event.currentTarget
    const enabled = label.dataset.enabled === 'true'
    
    if (!enabled) {
      event.preventDefault()
      event.stopPropagation()
      
      // Show flash message
      this.showFlashMessage('You do not have permission to access this data level. Please contact your administrator if you need higher access.', 'error')
      
      // Prevent the radio button from being selected
      const radio = label.querySelector('input[type="radio"]')
      if (radio) {
        radio.checked = false
      }
    }
  }

  showFlashMessage(message, type = 'notice') {
    // Remove any existing flash messages
    const existingFlash = document.querySelector('.flash-message')
    if (existingFlash) {
      existingFlash.remove()
    }
    
    // Create new flash message
    const flashDiv = document.createElement('div')
    flashDiv.className = `flash-message fixed top-4 right-4 z-50 max-w-md p-4 rounded-lg shadow-lg transition-all duration-300 ${
      type === 'error' ? 'bg-red-50 border border-red-200 text-red-800' : 'bg-blue-50 border border-blue-200 text-blue-800'
    }`
    
    flashDiv.innerHTML = `
      <div class="flex items-start">
        <div class="shrink-0">
          <svg class="h-5 w-5 ${type === 'error' ? 'text-red-400' : 'text-blue-400'}" viewBox="0 0 20 20" fill="currentColor">
            ${type === 'error' 
              ? '<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />'
              : '<path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />'}
          </svg>
        </div>
        <div class="ml-3 flex-1">
          <p class="text-sm font-medium">${message}</p>
        </div>
        <div class="ml-4 shrink-0 flex">
          <button onclick="this.closest('.flash-message').remove()" class="inline-flex text-gray-400 hover:text-gray-500">
            <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    `
    
    document.body.appendChild(flashDiv)
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      flashDiv.remove()
    }, 5000)
  }

  detectType() {
    const value = this.searchQueryTarget.value.replace(/\D/g, '') // Remove non-digits
    
    if (value.length === 15) {
      // Auto-select IMEI radio button
      const imeiRadio = this.element.querySelector('input[name="device_search_request[search_type]"][value="imei"]')
      if (imeiRadio) imeiRadio.checked = true
    } else if (value.length >= 9 && value.length <= 13) {
      // Auto-select MSISDN radio button
      const msisdnRadio = this.element.querySelector('input[name="device_search_request[search_type]"][value="msisdn"]')
      if (msisdnRadio) msisdnRadio.checked = true
    }
  }

  updateRequirements(event) {
    const requirements = JSON.parse(event.target.dataset.requirements || '[]')
    
    // Hide all requirement fields first
    if (this.hasRequirementsSectionTarget) {
      this.requirementsSectionTarget.classList.add('hidden')
    }
    if (this.hasCaseReferenceFieldTarget) {
      this.caseReferenceFieldTarget.classList.add('hidden')
    }
    if (this.hasExpirationFieldTarget) {
      this.expirationFieldTarget.classList.add('hidden')
    }
    
    // Show relevant fields based on requirements
    if (requirements.length > 0 && this.hasRequirementsSectionTarget) {
      this.requirementsSectionTarget.classList.remove('hidden')
      
      requirements.forEach(req => {
        if (req.includes('Case reference') && this.hasCaseReferenceFieldTarget) {
          this.caseReferenceFieldTarget.classList.remove('hidden')
        }
        if (req.includes('expiration date') && this.hasExpirationFieldTarget) {
          this.expirationFieldTarget.classList.remove('hidden')
        }
      })
    }
  }
}
